import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { X, Bug } from 'lucide-react';

const DebugPanel = () => {
  const [logs, setLogs] = useState([]);
  const [isVisible, setIsVisible] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);

  useEffect(() => {
    // Check if debug mode is enabled via URL parameter
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('debug') === 'true') {
      setIsEnabled(true);
      setIsVisible(true);
    }
  }, []);

  useEffect(() => {
    if (!isEnabled) return;

    // Capture console.log, console.error, console.warn
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;

    console.log = (...args) => {
      originalLog(...args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      setLogs(prev => [...prev, { 
        type: 'log', 
        message, 
        time: new Date().toLocaleTimeString() 
      }].slice(-50)); // Keep last 50 logs
    };

    console.error = (...args) => {
      originalError(...args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      setLogs(prev => [...prev, { 
        type: 'error', 
        message, 
        time: new Date().toLocaleTimeString() 
      }].slice(-50));
    };

    console.warn = (...args) => {
      originalWarn(...args);
      const message = args.map(arg => 
        typeof arg === 'object' ? JSON.stringify(arg, null, 2) : String(arg)
      ).join(' ');
      
      setLogs(prev => [...prev, { 
        type: 'warn', 
        message, 
        time: new Date().toLocaleTimeString() 
      }].slice(-50));
    };

    return () => {
      console.log = originalLog;
      console.error = originalError;
      console.warn = originalWarn;
    };
  }, [isEnabled]);

  const copyLogs = () => {
    const logsText = logs.map(log => 
      `[${log.time}] ${log.type.toUpperCase()}: ${log.message}`
    ).join('\n');
    
    navigator.clipboard.writeText(logsText).then(() => {
      alert('Logs copied to clipboard!');
    });
  };

  const clearLogs = () => {
    setLogs([]);
  };

  if (!isEnabled) return null;

  return (
    <>
      {/* Floating debug button */}
      {!isVisible && (
        <button
          onClick={() => setIsVisible(true)}
          className="fixed bottom-20 right-4 z-50 bg-purple-600 text-white p-3 rounded-full shadow-lg"
          aria-label="Show debug panel"
        >
          <Bug size={24} />
        </button>
      )}

      {/* Debug panel */}
      {isVisible && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-end">
          <div className="bg-white w-full h-[70vh] rounded-t-xl flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b">
              <div className="flex items-center gap-2">
                <Bug className="text-purple-600" size={20} />
                <h3 className="font-bold">Debug Console</h3>
                <span className="text-xs text-gray-500">({logs.length} logs)</span>
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyLogs}
                  className="text-xs"
                >
                  Copy
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={clearLogs}
                  className="text-xs"
                >
                  Clear
                </Button>
                <button
                  onClick={() => setIsVisible(false)}
                  className="p-1 hover:bg-gray-100 rounded"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Logs */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2 font-mono text-xs">
              {logs.length === 0 ? (
                <p className="text-gray-400 text-center py-8">
                  No logs yet. Try enabling notifications!
                </p>
              ) : (
                logs.map((log, index) => (
                  <div
                    key={index}
                    className={`p-2 rounded ${
                      log.type === 'error' 
                        ? 'bg-red-50 text-red-700 border border-red-200'
                        : log.type === 'warn'
                        ? 'bg-yellow-50 text-yellow-700 border border-yellow-200'
                        : 'bg-gray-50 text-gray-700 border border-gray-200'
                    }`}
                  >
                    <div className="flex items-start gap-2">
                      <span className="text-gray-400 text-[10px] mt-0.5">
                        {log.time}
                      </span>
                      <span className="font-semibold text-[10px]">
                        {log.type.toUpperCase()}
                      </span>
                    </div>
                    <pre className="whitespace-pre-wrap break-words mt-1">
                      {log.message}
                    </pre>
                  </div>
                ))
              )}
            </div>

            {/* Instructions */}
            <div className="p-3 border-t bg-gray-50 text-xs text-gray-600">
              <p>📱 <strong>How to use:</strong></p>
              <p>1. Click "Enable Notifications" button</p>
              <p>2. Watch logs appear here in real-time</p>
              <p>3. Tap "Copy" button and paste to send me the logs</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DebugPanel;
